import * as React from "react";
import { createPortal } from "react-dom";
import {
  CheckIcon,
  ChevronRightIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  GripVerticalIcon,
  Loader2Icon,
  PlusIcon,
  Rows3Icon,
  XIcon,
} from "lucide-react";
import {
  type Column,
  type ColumnPinningState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Cell,
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnOrderState,
  type ExpandedState,
  type FilterFn,
  type GroupingState,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  type Header,
  type Table as TanStackTable,
  type Updater,
  type VisibilityState,
} from "@tanstack/react-table";

import {
  DataTablePagination,
  DEFAULT_PAGE_SIZE_OPTIONS,
  shouldShowDataTablePaginationFooter,
  type DataTableServerPaginationConfig,
} from "./data-table-pagination";
import {
  DataTableAutomaticColumnHeader,
  DataTableColumnHeader,
  resolveDataTableColumnAlign,
} from "./data-table-column-header";
import { useRoutePaginationOwner } from "./use-route-pagination-owner";
import { useDataTableUrlPagination } from "./use-data-table-url-pagination";
import {
  DataTableColumnConfig,
  type DataTableColumnConfigColumn,
  type DataTableColumnConfigLabels,
} from "./data-table-column-config";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";
import { EmptyState } from "../../ui/empty-state";
import { Skeleton } from "../../ui/skeleton";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { useIsMobile } from "../../../lib/use-mobile";
import { mobileScreenHeaderStickyClassName } from "../../ui/mobile-screen-header";
import {
  DataTableLoadMore,
  DataTableMobileListContext,
  useDataTableMobileLabels,
  useLoadedPages,
  useLongPress,
} from "./data-table-mobile-list";
import { cn } from "../../../lib/utils";
import { surfaceCardClassName } from "../../ui/surface-card";
import { useLocation } from "react-router-dom";
import {
  DataTableConfiguredToolbar,
  type DataTableToolbarConfig,
} from "./data-table-configured-toolbar";

/** Desktop body region below thead — matches EmptyState viewport overlay inset. */
const DATA_TABLE_DESKTOP_BODY_TOP_OFFSET = "top-[2.625rem]";

/** Alternating-row tint for zebra striping; theme-aware (works on light + dark surfaces). */
const DATA_TABLE_ZEBRA_STRIPE_BG = "color-mix(in srgb, var(--muted) 34%, var(--background))";

/** Empty-state scroll viewport stabilizer only — not applied during loading (avoid bloating chrome). */
const DATA_TABLE_EMPTY_SCROLLVIEW_MIN_HEIGHT = "min-h-[14rem]";
const DATA_TABLE_INITIAL_LOADING_ROWS_DEFAULT = 3;
const DATA_TABLE_INITIAL_LOADING_ROWS_CAP = 4;

/** How a phone list draws its rows. */
export enum DataTableMobileListVariant {
  /** One card per row, with a gap between them (records you open: members, transactions). */
  Cards = "cards",
  /** One grouped card with the rows divided by hairlines (short reference lists: accounts, categories). */
  Grouped = "grouped",
}

/** How the desktop table reads. */
export enum DataTableVariant {
  /** Compact grid with column rules: ledgers, trial balances, journals and other accounting tables. */
  Dense = "dense",
  /**
   * Calm records list: comfortable rows, no column rules, muted headers, a soft hover tint. For tables of
   * things people open (budgets, forms, assets, team), paired with the tenant's alive cells.
   */
  Alive = "alive",
}

const ALIVE_HEAD_CLASSNAME = "h-11 max-h-11 border-r-0 !bg-card px-4 text-xs font-semibold text-muted-foreground";
const ALIVE_CELL_CLASSNAME = "h-14 border-r-0 px-4 py-2";
/** An embedded table's outer columns line up with its card's padding. */
const EMBEDDED_TABLE_CLASSNAME =
  "[&_th:first-child]:pl-[18px] [&_td:first-child]:pl-[18px] [&_th:last-child]:pr-[18px] [&_td:last-child]:pr-[18px]";
const ALIVE_ROW_CLASSNAME =
  "[--data-table-row-cell-bg:var(--card)] [--data-table-pinned-cell-bg:var(--card)] hover:[--data-table-row-cell-bg:color-mix(in_oklab,var(--muted)_55%,var(--card))] hover:[--data-table-pinned-cell-bg:color-mix(in_oklab,var(--muted)_55%,var(--card))]";

export type DataTableMobileConfig = {
  primaryField?: string;
  secondaryFields?: string[];
};

export type DataTablePersistentBottomRow = {
  id: string;
  cells: Record<string, React.ReactNode>;
  className?: string;
  /** Applied to every cell in this row (merged after pinning styles). Use to override the default `TableCell` surface (e.g. tinted totals). */
  cellClassName?: string;
  /**
   * Merged into each cell's `style` after pinning width/position.
   * Use when Tailwind `bg-primary/x` does not resolve (e.g. hex `--primary`) — `backgroundColor` here overrides `TableCell`'s `bg-background`.
   */
  cellStyle?: React.CSSProperties;
  mobileSummary?: React.ReactNode;
};

export type DataTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  initialColumnPinning?: ColumnPinningState;
  disablePinningPersistence?: boolean;
  emptyMessage?: string;
  pageSizeOptions?: number[];
  className?: string;
  tableContainerClassName?: string;
  tableClassName?: string;
  headerClassName?: string;
  headerRowClassName?: string;
  headClassName?: string;
  bodyClassName?: string;
  rowClassName?: string;
  /** Merged after `rowClassName` so rows can be styled from row data (e.g. cancelled transactions). */
  getRowClassName?: (row: Row<TData>) => string | undefined;
  /** Make the whole row clickable (e.g. open a detail drawer). Clicks on inner buttons/links still work. */
  onRowClick?: (row: TData) => void;
  cellClassName?: string;
  /** Apply a subtle alternating-row background (zebra striping) to improve row scanability. */
  zebra?: boolean;
  /** Dense (default) or the calm alive look, see {@link DataTableVariant}. Desktop only. */
  variant?: DataTableVariant;
  /**
   * Opt-in: the table is part of a card that already draws the frame (desktop). No border, radius or shadow of
   * its own; header and row dividers run edge to edge, and the first and last columns line up with the card's
   * 18px padding. Every other table is unchanged.
   */
  embedded?: boolean;
  /**
   * Opt-in: draw the toolbar into this element (a card header) instead of above the table. Pass the element from
   * a callback ref; until it is set the toolbar is not drawn.
   */
  toolbarContainer?: HTMLElement | null;
  initialSorting?: SortingState;
  sortingState?: SortingState;
  onSortingStateChange?: (sorting: SortingState) => void;
  sortingMode?: "client" | "server";
  initialPagination?: PaginationState;
  globalFilterFn?: FilterFn<TData>;
  persistPaginationInUrl?: boolean;
  paginationRole?: "auto" | "url" | "local";
  keepExplicitPaginationParams?: boolean;
  /** @deprecated Use paginationRole instead. */
  urlPaginationKey?: string;
  urlPaginationMode?: "replace" | "push";
  /**
   * `NoInfer` keeps TData anchored to `data`/`columns`: DataTableToolbarConfig declares TData as a
   * phantom type param, so without this a shared/generic toolbar config would infer TData as unknown
   * and make `columns` fail to typecheck.
   */
  toolbar?: ((table: TanStackTable<TData>) => React.ReactNode) | DataTableToolbarConfig<NoInfer<TData>>;
  actions?: (table: TanStackTable<TData>) => React.ReactNode;
  footer?: React.ReactNode;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  hidePagination?: boolean;
  /**
   * Server-driven pagination configuration. When provided, the pagination row
   * is rendered INSIDE the table's container (same border-t footer slot the
   * built-in client pagination uses), so callers don't need to mount a
   * separate `<DataTablePagination />` outside the table. Implies the table's
   * internal pagination row model is bypassed — pair with `hidePagination`
   * if you also want to suppress the client-side pagination fallback when
   * server data hasn't loaded yet.
   */
  serverPagination?: DataTableServerPaginationConfig;
  /** Custom summary text shown beside the server pagination controls (e.g. "1-10 of 14"). */
  serverPaginationSummaryText?: string;
  isLoading?: boolean;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  /** Picks which fields the auto-derived mobile card shows. Without it the card is derived from the columns. */
  mobileConfig?: DataTableMobileConfig;
  /** Custom mobile card renderer per row. When provided, replaces the generic label/value card on mobile and enables card mode even without `mobileConfig`. */
  renderMobileCard?: (row: Row<TData>) => React.ReactNode;
  /** Opt this table out of the default mobile card list, keeping the desktop (scrolling) table on phones. */
  disableMobileCards?: boolean;
  /**
   * The content of a phone row card (avatar, title, details). The card itself is the table's: tap to open
   * (`onRowClick`), long press to select, the row's ⋯ actions on the right. Without it the card is derived from
   * the columns (`mobileConfig` picks which).
   */
  renderMobileRow?: (row: Row<TData>) => React.ReactNode;
  /**
   * Desktop only: draws the page's rows in place of the table (a card grid, a list beside a preview…). The
   * toolbar, footer, selection and bulk actions stay the table's; the table frame is dropped so the view brings
   * its own surfaces. The view also draws the first load and the empty state (handed in), so anything it keeps
   * on screen, such as its own search field, survives a search that comes back empty.
   */
  renderDesktopBody?: (rows: Row<TData>[], state: DataTableDesktopBodyState) => React.ReactNode;
  /** Separate cards (default) or one grouped list, see {@link DataTableMobileListVariant}. */
  mobileListVariant?: DataTableMobileListVariant;
  /**
   * Phones replace the pager with "Load more", growing the list in place. Set false to keep paging, for lists
   * whose pages carry their own subtotals or running balances.
   */
  mobileLoadMore?: boolean;
  emptyState?: React.ReactNode;
  loadingRowCount?: number;
  persistentBottomRows?: DataTablePersistentBottomRow[];
  /**
   * Enable AG-Grid-style client-side row grouping (TanStack `getGroupedRowModel` + `getExpandedRowModel`):
   * - a **row-group panel** on top with a chip per grouped column (removable, drag-to-reorder) + an "Add group"
   *   menu and expand/collapse-all,
   * - a single **indented hierarchy column** (the source grouped columns are hidden while grouping is active),
   * - rolled-up values on group rows for any column with an `aggregationFn`/`aggregatedCell`.
   * Desktop-table only — the mobile card view always renders flat. Incompatible with `serverPagination`
   * (grouping needs the full dataset client-side).
   */
  enableGrouping?: boolean;
  /** Seed grouping with these column ids (order = nesting). The user can then add/remove/reorder via the panel. */
  initialGroupingColumns?: string[];
  /** Column ids offered in the "Add group" menu. Defaults to every column TanStack reports as groupable. */
  groupableColumns?: string[];
  /**
   * Leaf-level column whose value is shown (indented) inside the hierarchy column on data rows, mirroring AG Grid's
   * single-column layout. Its own column is hidden while grouping is active.
   */
  groupLeafColumnId?: string;
  /** Display label for a column in the group panel chips / Add menu. Defaults to the column's `meta.label` or string header. */
  getGroupColumnLabel?: (columnId: string) => string;
  /** i18n strings for the group panel (English defaults provided). */
  groupPanelLabels?: Partial<DataTableGroupPanelLabels>;
  /**
   * Allow reordering columns via a **column-config menu** (a popover with a dnd-kit reorderable list). The
   * select/actions/hierarchy columns stay fixed. Order is held for the table's lifetime (not persisted).
   */
  enableColumnReordering?: boolean;
  /** i18n strings for the column-config menu (English defaults provided). */
  columnConfigLabels?: Partial<DataTableColumnConfigLabels>;
  /**
   * Fires with the current visible **data** column ids (in display order; excludes select/actions/hierarchy)
   * whenever the user reorders columns, or when the column set resets. Lets a parent persist the order — e.g. to
   * make an export honor the on-screen column arrangement.
   */
  onColumnOrderChange?: (orderedDataColumnIds: string[]) => void;
};

export type DataTableGroupPanelLabels = {
  /** Header of the hierarchy column. */
  groupColumnHeader: string;
  /** Hint shown when nothing is grouped yet. */
  empty: string;
  addGroup: string;
  expandAll: string;
  collapseAll: string;
  removeGroup: string;
};

const DEFAULT_GROUP_PANEL_LABELS: DataTableGroupPanelLabels = {
  groupColumnHeader: "Group",
  empty: "Drag a column here or use “Add group” to group rows",
  addGroup: "Add group",
  expandAll: "Expand all",
  collapseAll: "Collapse all",
  removeGroup: "Remove group",
};

/** Per-row indentation (rem) for each grouping depth in the hierarchy column. */
const DATA_TABLE_GROUP_INDENT_REM = 1.1;

/** Synthetic id for the single indented hierarchy column shown while grouping is active. */
const DATA_TABLE_GROUP_COLUMN_ID = "__ibada_group__";

// Row grouping is temporarily suspended while it stabilises — the implementation stays in place but is forced off
// regardless of the per-table prop. Flip to `true` to re-enable. (Column reordering now uses the column-config
// menu instead of header drag, so it's no longer suspended.)
const DATA_TABLE_FEATURE_ENABLED: { grouping: boolean } = {
  grouping: false,
};


/** Page length when the caller does not choose one. */
const DEFAULT_INITIAL_PAGINATION: PaginationState = { pageIndex: 0, pageSize: 20 };
/** Rows a phone list shows at first, and adds per "Load more". */
const MOBILE_DEFAULT_PAGE_SIZE = 20;

export type DataTableDesktopBodyState = {
  /** Nothing loaded yet: draw placeholders. */
  isInitialLoading: boolean;
  /** The table's empty state (its message or the page's `emptyState`), for the view to place. */
  emptyState: React.ReactNode;
};

export function DataTable<TData>({
  data,
  columns,
  initialColumnPinning,
  disablePinningPersistence = false,
  emptyMessage = "No results found.",
  pageSizeOptions,
  className,
  tableContainerClassName,
  tableClassName,
  headerClassName,
  headerRowClassName,
  headClassName: headClassNameProp,
  bodyClassName,
  rowClassName: rowClassNameProp,
  getRowClassName,
  onRowClick,
  cellClassName: cellClassNameProp,
  zebra: zebraProp = true,
  variant = DataTableVariant.Dense,
  embedded = false,
  toolbarContainer,
  initialSorting = [],
  sortingState,
  onSortingStateChange,
  sortingMode = "client",
  initialPagination: initialPaginationProp,
  globalFilterFn,
  persistPaginationInUrl = true,
  paginationRole = "auto",
  keepExplicitPaginationParams = true,
  urlPaginationMode = "replace",
  toolbar,
  actions,
  footer,
  onRowSelectionChange,
  hidePagination = false,
  serverPagination,
  serverPaginationSummaryText,
  isLoading = false,
  globalFilter: controlledGlobalFilter,
  onGlobalFilterChange,
  mobileConfig,
  renderMobileCard,
  disableMobileCards = false,
  renderMobileRow,
  renderDesktopBody,
  mobileListVariant = DataTableMobileListVariant.Cards,
  mobileLoadMore = true,
  emptyState,
  loadingRowCount,
  persistentBottomRows = [],
  enableGrouping: enableGroupingProp = false,
  initialGroupingColumns,
  groupableColumns,
  groupLeafColumnId,
  getGroupColumnLabel,
  groupPanelLabels,
  enableColumnReordering = false,
  columnConfigLabels,
  onColumnOrderChange,
}: DataTableProps<TData>) {
  const isAlive = variant === DataTableVariant.Alive;
  const headClassName = cn(isAlive && ALIVE_HEAD_CLASSNAME, headClassNameProp);
  const rowClassName = cn(isAlive && ALIVE_ROW_CLASSNAME, rowClassNameProp);
  const cellClassName = cn(isAlive && ALIVE_CELL_CLASSNAME, cellClassNameProp);
  // Stripes fight the alive look's single hover tint.
  const zebra = isAlive ? false : zebraProp;
  // Row grouping stays suspended (see DATA_TABLE_FEATURE_ENABLED); column reordering is handled by the config menu.
  const enableGrouping = enableGroupingProp && DATA_TABLE_FEATURE_ENABLED.grouping;
  /*
   * Live on purpose even though card mode is off below: `effectiveColumnPinning` uses it to drop pinned
   * columns on a phone. A column pinned to the edge of a screen this narrow covers the data it is meant to
   * sit beside, so a horizontally scrolling table is only usable unpinned.
   */
  const isMobile = useIsMobile();
  // Every table is a card list on a phone (the redesigned mobile list), unless a screen opts out.
  const isMobileCards = isMobile && !disableMobileCards;
  const isCustomDesktopBody = !isMobileCards && Boolean(renderDesktopBody);
  const isFrameless = isMobileCards || isCustomDesktopBody;
  const [mobileSelecting, setMobileSelecting] = React.useState(false);
  // A server pager rendered inside the table (via `footer`) reports its page here; `serverPagination` brings its own.
  const [reportedServerPage, setReportedServerPage] = React.useState<number | null>(null);
  // A server pager passed as the `footer` is read directly, in the same render as the rows it pages: a page
  // reported back from inside the pager arrives a render late, and in that render the new page's empty
  // placeholder looked like a fresh page 1 and wiped the rows already loaded.
  const footerServerPage =
    React.isValidElement<{ server?: DataTableServerPaginationConfig }>(footer) && footer.type === DataTablePagination
      ? footer.props.server?.page
      : undefined;
  const mobileServerPage =
    isMobileCards && mobileLoadMore
      ? (serverPagination?.page ?? footerServerPage ?? reportedServerPage)
      : null;
  const loadedPages = useLoadedPages(data, mobileServerPage, isLoading);
  const pathname = useLocation().pathname;
  const reactId = React.useId();
  const tableId = React.useMemo(() => sanitizeKey(reactId), [reactId]);
  const isRoutePrimaryOwner = useRoutePaginationOwner(pathname, tableId);

  const shouldUseUrlPagination =
    persistPaginationInUrl && (paginationRole === "url" || (paginationRole === "auto" && isRoutePrimaryOwner));

  const [localSorting, setLocalSorting] = React.useState<SortingState>(initialSorting);
  const resolvedSorting = sortingState ?? localSorting;
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const allColumnIds = React.useMemo(() => collectColumnIds(columns), [columns]);
  const allowedColumnIds = React.useMemo(() => new Set(allColumnIds), [allColumnIds]);
  // A stable signature of the current column set — changes only when columns are added/removed/renamed (not on a
  // data refresh). Used to refresh column-derived menus and drop a stale custom order when the columns change
  // (e.g. a live report preview that adds or removes a column).
  const columnIdSignature = React.useMemo(() => allColumnIds.join("|"), [allColumnIds]);
  const pinningStorageKey = React.useMemo(
    () => `datatable:pinning:${sanitizeKey(`${pathname}:${tableId}`)}`,
    [pathname, tableId],
  );
  const defaultColumnPinning = React.useMemo<ColumnPinningState>(() => {
    const explicitPinning = normalizeColumnPinning(initialColumnPinning, allowedColumnIds);
    if ((explicitPinning.left ?? []).length > 0 || (explicitPinning.right ?? []).length > 0) {
      return explicitPinning;
    }

    const metaPinning = collectMetaDefaultPinning(columns, allowedColumnIds);
    if ((metaPinning.left ?? []).length > 0 || (metaPinning.right ?? []).length > 0) {
      return metaPinning;
    }

    return normalizeColumnPinning(undefined, allowedColumnIds);
  }, [allowedColumnIds, columns, initialColumnPinning]);
  const defaultColumnVisibility = React.useMemo<VisibilityState>(() => {
    const visibility: VisibilityState = {};
    columns.forEach((column) => {
      const columnId = getColumnDefId(column);
      if (!columnId) return;
      const meta = column.meta as { defaultHidden?: boolean; hidden?: boolean } | undefined;
      if (meta?.hidden || meta?.defaultHidden) {
        visibility[columnId] = false;
      }
    });
    return visibility;
  }, [columns]);

  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(() => defaultColumnVisibility);
  const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>(() => {
    if (disablePinningPersistence) {
      return defaultColumnPinning;
    }

    const persisted = readPersistedColumnPinning(pinningStorageKey, allowedColumnIds);
    return mergeColumnPinning(defaultColumnPinning, persisted, allowedColumnIds);
  });
  const initialPagination = initialPaginationProp ?? DEFAULT_INITIAL_PAGINATION;
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [localGlobalFilter, setLocalGlobalFilter] = React.useState("");
  const [localPagination, setLocalPagination] = React.useState<PaginationState>(initialPagination);
  const [grouping, setGrouping] = React.useState<GroupingState>(() =>
    enableGrouping ? (initialGroupingColumns ?? []) : [],
  );
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>([]);
  const resolvedGroupPanelLabels = React.useMemo<DataTableGroupPanelLabels>(
    () => ({ ...DEFAULT_GROUP_PANEL_LABELS, ...groupPanelLabels }),
    [groupPanelLabels],
  );
  const isGroupingActive = enableGrouping && grouping.length > 0;

  // A single indented hierarchy column (AG-Grid single-column layout). Prepended when grouping is enabled; its own
  // visibility is toggled with the grouping state below so it only appears while grouping is active.
  const autoGroupColumn = React.useMemo<ColumnDef<TData>>(
    () => ({
      id: DATA_TABLE_GROUP_COLUMN_ID,
      header: resolvedGroupPanelLabels.groupColumnHeader,
      enableGrouping: false,
      enableSorting: false,
      enableHiding: false,
      size: 280,
      minSize: 200,
      maxSize: 560,
      cell: ({ row }) => <AutoGroupCell row={row} leafColumnId={groupLeafColumnId} />,
    }),
    [groupLeafColumnId, resolvedGroupPanelLabels.groupColumnHeader],
  );
  const tableColumns = React.useMemo(
    () => (enableGrouping ? [autoGroupColumn, ...columns] : columns),
    [autoGroupColumn, columns, enableGrouping],
  );
  // Source grouped columns + the leaf column are hidden while grouping (their values render inside the hierarchy
  // column); the hierarchy column itself only shows while grouping is active. Forced keys are layered over the
  // user's own column-visibility so they never persist into it (see onColumnVisibilityChange below).
  const effectiveColumnVisibility = React.useMemo<VisibilityState>(() => {
    if (!enableGrouping) {
      return columnVisibility;
    }
    const next: VisibilityState = { ...columnVisibility, [DATA_TABLE_GROUP_COLUMN_ID]: isGroupingActive };
    if (isGroupingActive) {
      for (const columnId of grouping) {
        next[columnId] = false;
      }
      if (groupLeafColumnId) {
        next[groupLeafColumnId] = false;
      }
    }
    return next;
  }, [columnVisibility, enableGrouping, grouping, groupLeafColumnId, isGroupingActive]);
  const groupForcedHiddenIds = React.useMemo(
    () =>
      new Set(
        isGroupingActive
          ? [DATA_TABLE_GROUP_COLUMN_ID, ...grouping, ...(groupLeafColumnId ? [groupLeafColumnId] : [])]
          : enableGrouping
            ? [DATA_TABLE_GROUP_COLUMN_ID]
            : [],
      ),
    [enableGrouping, grouping, groupLeafColumnId, isGroupingActive],
  );
  const effectiveColumnPinning = React.useMemo<ColumnPinningState>(
    () => (isMobile ? { left: [], right: [] } : columnPinning),
    [columnPinning, isMobile],
  );
  const resolvedGlobalFilter = controlledGlobalFilter ?? localGlobalFilter;
  const globalFilterRef = React.useRef(resolvedGlobalFilter);
  const tableScrollerRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    globalFilterRef.current = resolvedGlobalFilter;
  }, [resolvedGlobalFilter]);

  // Keep the latest order-change callback in a ref so the column-reset effect can notify the parent without
  // depending on the callback's identity (which would otherwise re-fire the reset on every parent re-render).
  const onColumnOrderChangeRef = React.useRef(onColumnOrderChange);
  React.useEffect(() => {
    onColumnOrderChangeRef.current = onColumnOrderChange;
  }, [onColumnOrderChange]);

  React.useEffect(() => {
    const scroller = tableScrollerRef.current;
    if (!scroller) return;
    scroller.scrollLeft = 0;
  }, []);

  /**
   * Apply column-level `hidden` / `defaultHidden` when the column set changes.
   *
   * Same hazard as the pinning effect below: the updater built `{ ...prev }` unconditionally, so it never
   * compared equal, and its dependency was memoised on `columns`. An unstable `columns` prop turned that into
   * a render loop. Bail out by value, and key on the stable signature.
   */
  React.useEffect(() => {
    setColumnVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.entries(defaultColumnVisibility).forEach(([key, value]) => {
        if (value === false) {
          if (next[key] !== false) changed = true;
          next[key] = false;
          return;
        }
        if (!(key in next)) {
          next[key] = value;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnIdSignature]);

  /**
   * Re-resolve pinning when the COLUMN SET changes — and only then.
   *
   * This effect used to key on `allowedColumnIds` and `defaultColumnPinning`, both memoised on `columns`, and
   * its updater always returned a freshly built object. A caller that rebuilds its `columns` array each render
   * — an easy thing to do, and `columns` is a plain prop — therefore got: render -> new columns identity ->
   * effect -> setState with a never-equal object -> render, forever. React kills that with "Maximum update
   * depth exceeded", thrown from wherever it happens to be rather than from here.
   *
   * Two independent guards now, because a shared table must not melt down over a caller's memoisation:
   * `columnIdSignature` is a STRING, so re-creating the same columns is a no-op; and the updater bails out by
   * VALUE, returning the previous object when nothing actually changed. Either alone would stop the loop.
   */
  React.useEffect(() => {
    setColumnPinning((current) => {
      const next = disablePinningPersistence
        ? defaultColumnPinning
        : mergeColumnPinning(
            defaultColumnPinning,
            readPersistedColumnPinning(pinningStorageKey, allowedColumnIds),
            allowedColumnIds,
          );
      return columnPinningEquals(current, next) ? current : next;
    });
    // `defaultColumnPinning` and `allowedColumnIds` are derived from `columns`; keying on the stable signature
    // is what makes an unstable `columns` prop harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnIdSignature, disablePinningPersistence, pinningStorageKey]);

  React.useEffect(() => {
    if (disablePinningPersistence) return;
    writePersistedColumnPinning(pinningStorageKey, normalizeColumnPinning(columnPinning, allowedColumnIds));
  }, [allowedColumnIds, columnPinning, disablePinningPersistence, pinningStorageKey]);

  // When the underlying column set changes (e.g. a live report preview adds/removes a column), drop any custom
  // order so the table and the reorder menu reflect the new columns instead of a stale arrangement, and let the
  // parent clear any persisted order (the previous report's arrangement no longer applies).
  React.useEffect(() => {
    // Already empty means nothing to reset — passing a fresh `[]` would be a guaranteed state change and one
    // wasted render every time the column set shifts.
    setColumnOrder((prev) => (prev.length === 0 ? prev : []));
    onColumnOrderChangeRef.current?.([]);
  }, [columnIdSignature]);

  const handleGlobalFilterChange = React.useCallback(
    (updater: Updater<unknown>) => {
      const nextRaw = typeof updater === "function" ? updater(globalFilterRef.current) : updater;
      const nextValue = String(nextRaw ?? "");

      if (typeof controlledGlobalFilter !== "string") {
        setLocalGlobalFilter(nextValue);
      }

      onGlobalFilterChange?.(nextValue);
    },
    [controlledGlobalFilter, onGlobalFilterChange],
  );

  const { pagination: urlPagination, setPagination: setUrlPagination } = useDataTableUrlPagination({
    initialPagination,
    enabled: shouldUseUrlPagination,
    mode: urlPaginationMode,
    keepExplicitParams: keepExplicitPaginationParams,
  });

  const pagination = shouldUseUrlPagination ? urlPagination : localPagination;
  const setPagination = shouldUseUrlPagination ? setUrlPagination : setLocalPagination;

  /*
   * A phone shows one row at a time, so a 20-row page is mostly scrolling. Applied as an effect rather
   * than an initial value because `useIsMobile` resolves its media query after the first render — the
   * initial state is committed before we know the viewport.
   *
   * Only when the caller did not choose a size: a table that asks for a specific page length means it, and
   * a table the user has already re-paged is left alone.
   */
  const callerPageSize = initialPaginationProp?.pageSize;
  const hasAppliedMobilePageSize = React.useRef(false);
  React.useEffect(() => {
    if (!isMobile || callerPageSize || hasAppliedMobilePageSize.current) return;
    hasAppliedMobilePageSize.current = true;
    setPagination((previous) =>
      previous.pageSize === MOBILE_DEFAULT_PAGE_SIZE
        ? previous
        : { pageIndex: 0, pageSize: MOBILE_DEFAULT_PAGE_SIZE },
    );
  }, [isMobile, callerPageSize, setPagination]);

  const handleSortingChange = React.useCallback(
    (updater: Updater<SortingState>) => {
      const nextSorting = typeof updater === "function" ? updater(resolvedSorting) : updater;
      if (!sortingState) {
        setLocalSorting(nextSorting);
      }
      onSortingStateChange?.(nextSorting);
    },
    [onSortingStateChange, resolvedSorting, sortingState],
  );

  const table = useReactTable({
    // On a phone with "Load more", the pages loaded so far; otherwise exactly the rows given.
    data: loadedPages.rows,
    columns: tableColumns,
    defaultColumn: {
      minSize: 96,
      size: 160,
      maxSize: 520,
    },
    state: {
      sorting: resolvedSorting,
      columnFilters,
      columnVisibility: effectiveColumnVisibility,
      columnPinning: effectiveColumnPinning,
      rowSelection,
      globalFilter: resolvedGlobalFilter,
      pagination,
      grouping,
      expanded,
      columnOrder,
    },
    globalFilterFn,
    onGlobalFilterChange: handleGlobalFilterChange,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: setColumnFilters,
    // Strip the grouping-forced keys so they never leak into the user's persisted column visibility.
    onColumnVisibilityChange: (updater) => {
      const nextVisibility =
        typeof updater === "function" ? updater(effectiveColumnVisibility) : updater;
      const cleaned: VisibilityState = {};
      for (const [columnId, visible] of Object.entries(nextVisibility)) {
        if (!groupForcedHiddenIds.has(columnId)) {
          cleaned[columnId] = visible;
        }
      }
      setColumnVisibility(cleaned);
    },
    onColumnPinningChange: setColumnPinning,
    onGroupingChange: setGrouping,
    onRowSelectionChange: (updater) => {
      const nextSelection = typeof updater === "function" ? updater(rowSelection) : updater;
      setRowSelection(nextSelection);
      onRowSelectionChange?.(nextSelection);
    },
    onPaginationChange: setPagination,
    onExpandedChange: setExpanded,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: sortingMode === "client" ? getSortedRowModel() : undefined,
    manualSorting: sortingMode === "server",
    // Grouping models are only installed when opted in (keeps the default table path untouched). Grouped columns
    // stay in place (`groupedColumnMode: false`) so grouping coexists with column pinning instead of reordering.
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    getExpandedRowModel: enableGrouping ? getExpandedRowModel() : undefined,
    groupedColumnMode: false as const,
    // When the footer is hidden the caller owns pagination (server-side) and
    // passes only the current page of rows. Installing the pagination row model
    // here would re-slice that already-sliced page (e.g. URL ?page drives an
    // internal pageIndex past the page boundary -> empty page 2). Skip it so the
    // table renders the rows it was given verbatim.
    // Accumulated phone pages must not be re-sliced to one page either.
    getPaginationRowModel: hidePagination || mobileServerPage !== null ? undefined : getPaginationRowModel(),
  });

  // Display label for a column in the group panel (chip / Add menu): explicit override → meta.label → string header.
  const resolveGroupLabel = React.useCallback(
    (columnId: string) => {
      const override = getGroupColumnLabel?.(columnId);
      if (override) {
        return override;
      }
      const column = table.getColumn(columnId);
      const meta = column?.columnDef.meta as { label?: string } | undefined;
      if (meta?.label) {
        return meta.label;
      }
      const header = column?.columnDef.header;
      return typeof header === "string" ? header : columnId;
    },
    [getGroupColumnLabel, table],
  );
  // Columns offered in the "Add group" menu: the caller's allow-list (or every groupable column), minus the
  // synthetic hierarchy column and anything already grouped.
  const groupableColumnOptions = React.useMemo(() => {
    const ids =
      groupableColumns ??
      table
        .getAllLeafColumns()
        .filter((column) => column.getCanGroup() && column.id !== DATA_TABLE_GROUP_COLUMN_ID)
        .map((column) => column.id);
    return ids.filter((id) => !grouping.includes(id));
  }, [grouping, groupableColumns, table]);

  // Column reordering via the config menu. Reorderable = everything except the fixed select/actions/hierarchy
  // columns (and any pinned column, which keeps its edge position).
  const isReorderableColumn = React.useCallback(
    (columnId: string, isPinned: boolean) =>
      !isPinned && columnId !== "select" && columnId !== "actions" && columnId !== DATA_TABLE_GROUP_COLUMN_ID,
    [],
  );
  const reorderableColumns = React.useMemo<DataTableColumnConfigColumn[]>(() => {
    if (!enableColumnReordering) {
      return [];
    }
    // Use the VISIBLE leaf columns so the menu reflects the current on-screen order (getAllLeafColumns ignores
    // columnOrder). `table` is a stable ref, so we also depend on the column signature / visibility / grouping —
    // otherwise the menu would not refresh when columns are added, removed, hidden, or grouped.
    return table
      .getVisibleLeafColumns()
      .filter((column) => isReorderableColumn(column.id, Boolean(column.getIsPinned())))
      .map((column) => ({ id: column.id, label: resolveGroupLabel(column.id) }));
  }, [
    columnIdSignature,
    columnOrder,
    effectiveColumnVisibility,
    enableColumnReordering,
    grouping,
    isReorderableColumn,
    resolveGroupLabel,
    table,
  ]);
  // The on-screen data columns (exclude the fixed select/actions/hierarchy columns) in display order.
  const dataColumnIdsFrom = React.useCallback(
    (ids: string[]) =>
      ids.filter((id) => id !== "select" && id !== "actions" && id !== DATA_TABLE_GROUP_COLUMN_ID),
    [],
  );
  // Apply a new order of the reorderable columns, keeping fixed columns in their slots.
  const applyColumnReorder = React.useCallback(
    (orderedReorderableIds: string[]) => {
      const fullOrder = table.getVisibleLeafColumns().map((column) => column.id);
      const queue = [...orderedReorderableIds];
      const reorderableSet = new Set(reorderableColumns.map((column) => column.id));
      const next = fullOrder.map((id) => (reorderableSet.has(id) ? queue.shift() ?? id : id));
      setColumnOrder(next);
      onColumnOrderChange?.(dataColumnIdsFrom(next));
    },
    [dataColumnIdsFrom, onColumnOrderChange, reorderableColumns, table],
  );

  const totalFilteredRows = table.getFilteredRowModel().rows.length;
  const hasObservedDataRef = React.useRef(false);
  React.useEffect(() => {
    if (totalFilteredRows > 0) {
      hasObservedDataRef.current = true;
    }
  }, [totalFilteredRows]);

  const pageCount = table.getPageCount();
  React.useEffect(() => {
    // When the caller renders its own pagination footer (server-side
    // pagination), the DataTable only receives the current page of
    // rows — `pageCount` here would always be 1 and would silently
    // clamp the externally-managed page index back to 0 on every
    // click. Skip the clamp entirely in that mode.
    if (hidePagination) {
      return;
    }
    if (!hasObservedDataRef.current && totalFilteredRows === 0) {
      return;
    }

    if (totalFilteredRows === 0) {
      return;
    }

    const maxPageIndex = Math.max(pageCount - 1, 0);
    if (pagination.pageIndex > maxPageIndex) {
      setPagination((prev) => ({ ...prev, pageIndex: maxPageIndex }));
    }
  }, [hidePagination, pageCount, pagination.pageIndex, setPagination, totalFilteredRows]);

  const visibleLeafColumns = table.getVisibleLeafColumns();
  const visibleColumnCount = Math.max(visibleLeafColumns.length, columns.length, 1);
  const visibleColumnIds = React.useMemo(() => visibleLeafColumns.map((column) => column.id), [visibleLeafColumns]);
  const rows = table.getRowModel().rows;
  const hasPersistentBottomRows = persistentBottomRows.length > 0;
  const isInitialLoading = isLoading && rows.length === 0;
  const showLoadingOverlay = isLoading && rows.length > 0;
  const initialLoadingSkeletonRows = Math.min(
    Math.max(loadingRowCount ?? DATA_TABLE_INITIAL_LOADING_ROWS_DEFAULT, DATA_TABLE_INITIAL_LOADING_ROWS_DEFAULT),
    DATA_TABLE_INITIAL_LOADING_ROWS_CAP,
  );
  const resolvedEmptyState = emptyState ?? <EmptyState title={emptyMessage} compact surface={false} className='bg-transparent' />;
  /** Keeps empty UI centered in the scroll viewport instead of drifting with wide colspan rows. */
  const showDesktopEmptyViewportOverlay = !isInitialLoading && rows.length === 0 && !hasPersistentBottomRows;
  const useDesktopCompactTableScrollHeight = showDesktopEmptyViewportOverlay;
  const resolvedPageSizeOptions =
    Array.isArray(pageSizeOptions) && pageSizeOptions.length > 0 ? pageSizeOptions : DEFAULT_PAGE_SIZE_OPTIONS;
  const showPaginationFooter =
    !hidePagination && shouldShowDataTablePaginationFooter(resolvedPageSizeOptions, totalFilteredRows);
  const handleSetMobileSelecting = React.useCallback(
    (next: boolean) => {
      setMobileSelecting(next);
      if (!next) table.resetRowSelection();
    },
    [table],
  );
  // Leaving the last selection (unticking it, or a bulk action that clears it) ends selection mode, as in any
  // phone app. Entering from the ⋯ menu starts at zero and stays until something is picked and cleared.
  const selectedRowCount = table.getFilteredSelectedRowModel().rows.length;
  const previousSelectedCount = React.useRef(selectedRowCount);
  React.useEffect(() => {
    if (mobileSelecting && previousSelectedCount.current > 0 && selectedRowCount === 0) setMobileSelecting(false);
    previousSelectedCount.current = selectedRowCount;
  }, [mobileSelecting, selectedRowCount]);
  const mobileListContext = React.useMemo(
    () => ({
      loadMore: isMobileCards && mobileLoadMore,
      reportServerPage: setReportedServerPage,
      shownCount: rows.length,
      loadedPage: loadedPages.loadedPage,
      selecting: isMobileCards && mobileSelecting,
      setSelecting: handleSetMobileSelecting,
    }),
    [handleSetMobileSelecting, isMobileCards, loadedPages.loadedPage, mobileLoadMore, mobileSelecting, rows.length],
  );

  // A phone list pins its whole toolbar (title, search, chips): the toolbar, not the title row inside it,
  // because a sticky element only sticks within its parent. A tab's list has no title of its own (the big
  // header is its title), but its search and chips still stay put while the rows scroll. An intro block inside
  // the toolbar (Activities' "This week") would hold half the screen still, so such a list scrolls as one.
  const pinMobileToolbar = Boolean(
    isMobile &&
      typeof toolbar === "object" &&
      (toolbar.mobileHeader || toolbar.search || toolbar.views?.items.length) &&
      !toolbar.mobileIntro,
  );

  const toolbarBlock = (
    <div
      // Pinned, it carries the page's top padding itself (see MobileScreenHeader's data-page-sticky-header).
      data-page-sticky-header={pinMobileToolbar ? "" : undefined}
      className={cn(
        "flex min-w-0 items-center justify-between gap-2",
        pinMobileToolbar && mobileScreenHeaderStickyClassName,
      )}
    >
      <div className='min-w-0 flex-1'>
        {typeof toolbar === "function"
          ? toolbar(table)
          : toolbar
            ? <DataTableConfiguredToolbar table={table} config={toolbar} />
            : null}
      </div>
      {enableColumnReordering && reorderableColumns.length > 0 ? (
        <DataTableColumnConfig
          columns={reorderableColumns}
          onReorder={applyColumnReorder}
          onReset={() => setColumnOrder([])}
          labels={columnConfigLabels}
        />
      ) : null}
    </div>
  );

  return (
    <DataTableMobileListContext.Provider value={mobileListContext}>
    <div className={cn("flex w-full min-w-0 flex-col gap-3", className)}>
      {toolbar || (enableColumnReordering && reorderableColumns.length > 0) ? (
        toolbarContainer !== undefined ? (
          toolbarContainer ? createPortal(toolbarBlock, toolbarContainer) : null
        ) : (
          toolbarBlock
        )
      ) : null}
      {enableGrouping ? (
        <DataTableGroupPanel
          grouping={grouping}
          onChange={setGrouping}
          options={groupableColumnOptions}
          resolveLabel={resolveGroupLabel}
          labels={resolvedGroupPanelLabels}
          allExpanded={table.getIsAllRowsExpanded()}
          onExpandAll={() => table.toggleAllRowsExpanded(true)}
          onCollapseAll={() => table.toggleAllRowsExpanded(false)}
        />
      ) : null}
      <div
        className={cn(
          "w-full overflow-hidden",
          // Drop the table frame ONLY when actually rendering mobile cards (they bring their own surface);
          // the desktop table (and mobile when cards are disabled) keeps the framed look like every other table.
          // The frame is the SHARED card surface, so a table and a dashboard widget on the same page match.
          !isFrameless && !embedded && surfaceCardClassName,
          !isFrameless && !embedded && "shadow-[var(--shadow-table)]",
        )}
      >
        <div className='relative max-w-full overflow-hidden'>
          <div className={cn("overflow-hidden", !isFrameless && "bg-card")}>
            <div className={cn("relative overflow-hidden", !isFrameless && "bg-card")}>
              {isMobileCards ? (
                <DataTableMobileCards
                  rows={rows}
                  mobileConfig={mobileConfig}
                  renderMobileCard={renderMobileCard}
                  renderMobileRow={renderMobileRow}
                  variant={mobileListVariant}
                  onRowClick={onRowClick}
                  selecting={mobileSelecting}
                  onStartSelecting={() => setMobileSelecting(true)}
                  isInitialLoading={isInitialLoading}
                  skeletonRows={initialLoadingSkeletonRows}
                  emptyState={resolvedEmptyState}
                  persistentBottomRows={persistentBottomRows}
                />
              ) : isCustomDesktopBody && renderDesktopBody ? (
                renderDesktopBody(rows, { isInitialLoading, emptyState: resolvedEmptyState })
              ) : (
              <>
              <div
                ref={tableScrollerRef}
                className={cn(
                  "relative max-w-full overflow-auto",
                  useDesktopCompactTableScrollHeight && DATA_TABLE_EMPTY_SCROLLVIEW_MIN_HEIGHT,
                  tableContainerClassName,
                )}
              >
                {/* table-auto + w-max: columns size to their content and the table grows past the
                    container (which scrolls horizontally) instead of forcing fixed widths that truncate. */}
                <Table
                  className={cn(
                    "w-max min-w-full table-auto",
                    embedded && !isMobileCards && EMBEDDED_TABLE_CLASSNAME,
                    tableClassName,
                  )}
                >
                  <TableHeader className={cn("bg-card", headerClassName)}>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id} className={headerRowClassName}>
                        {headerGroup.headers.map((header) => {
                          const pinningStyle = getPinnedColumnStyle(header.column, "header");
                          const headerContent = renderHeaderContent(header);
                          return (
                            <TableHead
                              key={header.id}
                              className={cn("bg-muted/25", pinningStyle.className, headClassName)}
                              style={resolveColumnStyle(header.column, pinningStyle.style)}
                            >
                              {headerContent}
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody
                    aria-busy={isInitialLoading}
                    className={cn(
                      "bg-card",
                      showLoadingOverlay && "pointer-events-none blur-[1px] opacity-75",
                      bodyClassName,
                    )}
                  >
                    {isInitialLoading ? (
                      renderLoadingTableRows(visibleLeafColumns, initialLoadingSkeletonRows, cellClassName)
                    ) : rows.length || hasPersistentBottomRows ? (
                      <>
                        {rows.length ? (
                          rows.map((row, rowIndex) => (
                            <TableRow
                              key={row.id}
                              data-state={row.getIsSelected() && "selected"}
                              onClick={
                                onRowClick
                                  ? (event) => {
                                      // React bubbles clicks from portals (a row's ⋯ menu, its dialogs) up to
                                      // the row; only a click on the row's own DOM opens it.
                                      if (!event.currentTarget.contains(event.target as Node)) {
                                        return;
                                      }
                                      if ((event.target as HTMLElement).closest("a,button,[role=button],input,label")) {
                                        return;
                                      }
                                      onRowClick(row.original);
                                    }
                                  : undefined
                              }
                              className={cn(
                                "group bg-card",
                                onRowClick && "cursor-pointer",
                                row.getIsGrouped() && "bg-muted/40 font-medium",
                                rowClassName,
                                getRowClassName?.(row),
                              )}
                              style={
                                zebra && rowIndex % 2 === 1
                                  ? ({
                                      "--data-table-row-cell-bg": DATA_TABLE_ZEBRA_STRIPE_BG,
                                      "--data-table-pinned-cell-bg": DATA_TABLE_ZEBRA_STRIPE_BG,
                                    } as React.CSSProperties)
                                  : undefined
                              }
                            >
                              {row.getVisibleCells().map((cell) => {
                                const pinningStyle = getPinnedColumnStyle(cell.column, "cell");
                                const resolvedCellStyle = resolveColumnStyle(cell.column, pinningStyle.style);
                                return (
                                  <TableCell
                                    key={cell.id}
                                    className={cn(
                                      pinningStyle.className,
                                      cellClassName,
                                      readColumnMetaCellClassName(cell.column),
                                    )}
                                    style={{
                                      backgroundColor: "var(--data-table-row-cell-bg, var(--background))",
                                      ...resolvedCellStyle,
                                    }}
                                  >
                                    {renderDataCellContent(cell)}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={visibleColumnCount}
                              className={cn("px-4 py-6 align-middle whitespace-normal", cellClassName)}
                            >
                              {resolvedEmptyState}
                            </TableCell>
                          </TableRow>
                        )}

                        {persistentBottomRows.map((persistentRow) => (
                          <TableRow
                            key={`persistent-bottom-row-${persistentRow.id}`}
                            className={cn("group bg-card hover:bg-muted/20", persistentRow.className)}
                          >
                            {visibleLeafColumns.map((column) => {
                              const pinningStyle = getPinnedColumnStyleForPersistentBottomRow(column);
                              return (
                                <TableCell
                                  key={`persistent-bottom-row-${persistentRow.id}-${column.id}`}
                                  className={cn(
                                    pinningStyle.className,
                                    persistentRow.cellClassName,
                                    cellClassName,
                                    readColumnMetaCellClassName(column),
                                  )}
                                  style={{
                                    ...resolveColumnStyle(column, pinningStyle.style),
                                    ...persistentRow.cellStyle,
                                  }}
                                >
                                  {persistentRow.cells[column.id] ?? null}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </>
                    ) : showDesktopEmptyViewportOverlay ? (
                      <TableRow aria-hidden='true' className='border-0 hover:bg-transparent [&_td]:border-0'>
                        <TableCell colSpan={visibleColumnCount} className='h-px p-0' />
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={visibleColumnCount}
                          className={cn("px-4 py-6 align-middle whitespace-normal", cellClassName)}
                        >
                          {resolvedEmptyState}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {showDesktopEmptyViewportOverlay ? (
                  <div
                    className={cn(
                      "pointer-events-none absolute inset-x-0 bottom-0 z-[2] flex items-center justify-center bg-background",
                      DATA_TABLE_DESKTOP_BODY_TOP_OFFSET,
                    )}
                    role='status'
                    aria-live='polite'
                  >
                    <div className='pointer-events-auto'>{resolvedEmptyState}</div>
                  </div>
                ) : null}
              </div>

              </>
              )}
            </div>

            {footer ? <div className={cn(!isFrameless && "border-t border-[color:var(--table-border)]")}>{footer}</div> : null}
            {serverPagination ? (
              <div className={cn(!isMobileCards && "border-t border-[color:var(--table-border)]")}>
                <DataTablePagination
                  server={serverPagination}
                  pageSizeOptions={resolvedPageSizeOptions}
                  summaryText={serverPaginationSummaryText}
                />
              </div>
            ) : isMobileCards && mobileLoadMore && !hidePagination ? (
              // A client-side list grows by a page per tap instead of paging.
              <DataTableLoadMore
                shown={rows.length}
                total={totalFilteredRows}
                canLoadMore={rows.length < totalFilteredRows}
                loading={false}
                onLoadMore={() =>
                  setPagination((previous) => ({
                    pageIndex: 0,
                    pageSize: (previous.pageIndex + 1) * previous.pageSize + MOBILE_DEFAULT_PAGE_SIZE,
                  }))
                }
              />
            ) : showPaginationFooter ? (
              <div className='border-t border-[color:var(--table-border)]'>
                <DataTablePagination table={table} pageSizeOptions={resolvedPageSizeOptions} />
              </div>
            ) : null}
          </div>

          {showLoadingOverlay ? (
            <div className='bg-muted/55 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px]'>
              <Loader2Icon className='text-muted-foreground size-5 animate-spin' />
            </div>
          ) : null}
        </div>

        {actions ? actions(table) : null}
      </div>
    </div>
    </DataTableMobileListContext.Provider>
  );
}

function sanitizeKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "-");
}

function collectColumnIds<TData>(columns: ColumnDef<TData>[]): string[] {
  const result: string[] = [];
  const visit = (columnDefs: ColumnDef<TData>[]) => {
    columnDefs.forEach((columnDef) => {
      const columnId = getColumnDefId(columnDef);
      if (columnId) {
        result.push(columnId);
      }

      if ("columns" in columnDef && Array.isArray(columnDef.columns)) {
        visit(columnDef.columns as ColumnDef<TData>[]);
      }
    });
  };
  visit(columns);
  return result;
}

function collectMetaDefaultPinning<TData>(
  columns: ColumnDef<TData>[],
  allowedColumnIds: Set<string>,
): ColumnPinningState {
  const pinning: ColumnPinningState = { left: [], right: [] };
  const visit = (columnDefs: ColumnDef<TData>[]) => {
    columnDefs.forEach((columnDef) => {
      const columnId = getColumnDefId(columnDef);
      const meta = columnDef.meta as { defaultPin?: "left" | "right" } | undefined;
      if (columnId && meta?.defaultPin && allowedColumnIds.has(columnId)) {
        if (meta.defaultPin === "left") {
          pinning.left?.push(columnId);
        } else {
          pinning.right?.push(columnId);
        }
      }

      if ("columns" in columnDef && Array.isArray(columnDef.columns)) {
        visit(columnDef.columns as ColumnDef<TData>[]);
      }
    });
  };

  visit(columns);
  return normalizeColumnPinning(pinning, allowedColumnIds);
}

function normalizeColumnPinning(
  columnPinning: ColumnPinningState | undefined,
  allowedColumnIds: Set<string>,
): ColumnPinningState {
  const left = Array.from(new Set((columnPinning?.left ?? []).filter((columnId) => allowedColumnIds.has(columnId))));
  const configuredRight = Array.from(
    new Set(
      (columnPinning?.right ?? []).filter((columnId) => allowedColumnIds.has(columnId) && !left.includes(columnId)),
    ),
  );
  const right = configuredRight.filter((columnId) => !left.includes(columnId));

  return { left, right };
}

/** Do two pinning states pin the same columns, in the same order, on the same sides? */
function columnPinningEquals(left: ColumnPinningState, right: ColumnPinningState): boolean {
  const same = (a: string[] | undefined, b: string[] | undefined) => {
    const x = a ?? [];
    const y = b ?? [];
    return x.length === y.length && x.every((value, index) => value === y[index]);
  };
  return same(left.left, right.left) && same(left.right, right.right);
}

function mergeColumnPinning(
  basePinning: ColumnPinningState,
  additionalPinning: ColumnPinningState | null,
  allowedColumnIds: Set<string>,
): ColumnPinningState {
  if (!additionalPinning) {
    return normalizeColumnPinning(basePinning, allowedColumnIds);
  }

  return normalizeColumnPinning(
    {
      left: [...(basePinning.left ?? []), ...(additionalPinning.left ?? [])],
      right: [...(basePinning.right ?? []), ...(additionalPinning.right ?? [])],
    },
    allowedColumnIds,
  );
}

function readPersistedColumnPinning(storageKey: string, allowedColumnIds: Set<string>): ColumnPinningState | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const serialized = window.localStorage.getItem(storageKey);
    if (!serialized) return null;
    const parsed = JSON.parse(serialized) as ColumnPinningState;
    const normalized = normalizeColumnPinning(parsed, allowedColumnIds);
    return (normalized.left ?? []).length > 0 || (normalized.right ?? []).length > 0 ? normalized : null;
  } catch {
    return null;
  }
}

function writePersistedColumnPinning(storageKey: string, columnPinning: ColumnPinningState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(columnPinning));
  } catch {
    // Ignore storage write failures (private mode, quota, etc.).
  }
}

function getPinnedColumnStyle<TData>(column: Column<TData, unknown>, variant: "header" | "cell") {
  const pinned = column.getIsPinned();
  if (!pinned) {
    return { className: "", style: undefined as React.CSSProperties | undefined };
  }

  const columnSize = column.getSize();
  const style: React.CSSProperties = {
    position: "sticky",
    zIndex: variant === "header" ? 30 : 20,
    /**
     * Match unpinned `<TableCell>` by default, while allowing highlighted rows to carry their
     * background into pinned action cells.
     */
    backgroundColor:
      variant === "header"
        ? "color-mix(in srgb, var(--muted) 25%, var(--card))"
        : "var(--data-table-pinned-cell-bg, var(--background))",
    width: columnSize,
    minWidth: column.columnDef.minSize ?? columnSize,
  };

  if (pinned === "left") {
    style.left = `${column.getStart("left")}px`;
  } else {
    style.right = `${column.getAfter("right")}px`;
  }

  const isBoundary =
    (pinned === "left" && column.getIsLastColumn("left")) || (pinned === "right" && column.getIsFirstColumn("right"));

  return {
    className: cn(
      "relative",
      variant === "header" ? "bg-muted/25" : "bg-card group-hover:bg-muted/20 group-data-[state=selected]:bg-muted/30",
      // Right-pinned boundary cells float over their left neighbor when the table overflows, hiding
      // the neighbor's border-r — so the separator must live on the pinned cell itself. A -1px
      // box-shadow (not border-l) paints on the same pixel as the neighbor's border when the cells
      // sit flush, avoiding a doubled 2px line. Left-pinned cells carry their own border-r already.
      isBoundary &&
        pinned === "right" &&
        "shadow-[var(--table-pinned-divider-shadow)]",
    ),
    style,
  };
}

/** Sticky cells on persistent footer rows: no baked-in surface fill — use row `cellClassName` / `cellStyle`. */
function getPinnedColumnStyleForPersistentBottomRow<TData>(column: Column<TData, unknown>) {
  const pinned = column.getIsPinned();
  if (!pinned) {
    return { className: "", style: undefined as React.CSSProperties | undefined };
  }

  const columnSize = column.getSize();
  const style: React.CSSProperties = {
    position: "sticky",
    zIndex: 20,
    width: columnSize,
    minWidth: column.columnDef.minSize ?? columnSize,
  };

  if (pinned === "left") {
    style.left = `${column.getStart("left")}px`;
  } else {
    style.right = `${column.getAfter("right")}px`;
  }

  const isBoundary =
    (pinned === "left" && column.getIsLastColumn("left")) || (pinned === "right" && column.getIsFirstColumn("right"));

  return {
    className: cn(
      "relative",
      isBoundary &&
        (pinned === "left"
          ? "shadow-[1px_0_0_0_color-mix(in_srgb,var(--border)_60%,transparent)]"
          : "shadow-[-1px_0_0_0_color-mix(in_srgb,var(--border)_60%,transparent)]"),
    ),
    style,
  };
}

function resolveColumnStyle<TData>(
  column: Column<TData, unknown>,
  baseStyle: React.CSSProperties | undefined,
): React.CSSProperties | undefined {
  const minWidth = column.columnDef.minSize ?? 96;
  // Content-based sizing: only pin a fixed `width` when a column explicitly opts into a `size`
  // (e.g. the select/actions columns); otherwise let it size to its content (the table is table-auto
  // and overflow scrolls horizontally). Pinned columns still get an exact width from the pinning style.
  const explicitSize = column.columnDef.size;
  const sizing: React.CSSProperties = explicitSize != null ? { width: explicitSize, minWidth } : { minWidth };

  return { ...sizing, ...baseStyle };
}

function readColumnMetaCellClassName<TData>(column: Column<TData, unknown>) {
  const meta = column.columnDef.meta as { cellClassName?: string; align?: "left" | "right" } | undefined;
  // `meta.align` aligns the whole column; a cell's content (text, an inline-flex Badge, a link) follows.
  // Resolved through the SAME helper the header uses, so a money column that sets no meta cannot end up with a
  // right-aligned header over left-aligned figures.
  const align = resolveDataTableColumnAlign(column.id, meta?.align);
  return cn(align === "right" && "text-right", meta?.cellClassName);
}

/**
 * Mobile rendering of a DataTable: a list of row cards (see {@link DataTableMobileRowCard}) reusing each
 * column's own `cell` renderer, so links, badges and menus behave as on desktop. Driven by the table's
 * {@link DataTableMobileConfig} or `renderMobileRow`.
 */
function DataTableMobileCards<TData>({
  rows,
  mobileConfig,
  renderMobileCard,
  renderMobileRow,
  variant,
  onRowClick,
  selecting,
  onStartSelecting,
  isInitialLoading,
  skeletonRows,
  emptyState,
  persistentBottomRows,
}: {
  rows: Row<TData>[];
  mobileConfig?: DataTableMobileConfig;
  renderMobileCard?: (row: Row<TData>) => React.ReactNode;
  renderMobileRow?: (row: Row<TData>) => React.ReactNode;
  variant: DataTableMobileListVariant;
  onRowClick?: (row: TData) => void;
  selecting: boolean;
  onStartSelecting: () => void;
  isInitialLoading: boolean;
  skeletonRows: number;
  emptyState: React.ReactNode;
  persistentBottomRows: DataTablePersistentBottomRow[];
}) {
  const grouped = variant === DataTableMobileListVariant.Grouped;
  const listClassName = grouped ? "bg-card divide-border/60 divide-y overflow-hidden rounded-[1.25rem]" : "space-y-2";

  if (isInitialLoading) {
    return (
      <div className={listClassName}>
        {Array.from({ length: skeletonRows }).map((_, index) => (
          <div
            key={`mcard-skeleton-${index}`}
            className={cn("bg-card flex items-center gap-3 px-3.5 py-3", !grouped && "rounded-[1.125rem]")}
          >
            <div className='bg-muted size-11 shrink-0 animate-pulse rounded-full' />
            <div className='flex-1 space-y-2'>
              <div className='bg-muted h-4 w-2/5 animate-pulse rounded' />
              <div className='bg-muted/70 h-3 w-3/5 animate-pulse rounded' />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return <div className='px-4 py-10'>{emptyState}</div>;
  }

  return (
    <>
    <div className={listClassName}>
      {rows.map((row) =>
        renderMobileCard ? (
          // Fully custom card: the screen owns the whole surface and its interactions.
          <div key={row.id} data-state={row.getIsSelected() ? "selected" : undefined}>
            {renderMobileCard(row)}
          </div>
        ) : (
          <DataTableMobileRowCard
            key={row.id}
            row={row}
            mobileConfig={mobileConfig}
            renderMobileRow={renderMobileRow}
            grouped={grouped}
            onRowClick={onRowClick}
            selecting={selecting}
            onStartSelecting={onStartSelecting}
          />
        ),
      )}
    </div>
    {persistentBottomRows.some((persistentRow) => persistentRow.mobileSummary) ? (
      <div className='mt-2 space-y-2'>
        {persistentBottomRows.map((persistentRow) =>
          persistentRow.mobileSummary ? (
            <div key={`mcard-summary-${persistentRow.id}`} className='bg-muted/40 rounded-2xl px-3.5 py-3 text-sm'>
              {persistentRow.mobileSummary}
            </div>
          ) : null,
        )}
      </div>
    ) : null}
    </>
  );
}

/**
 * One phone row: a card the whole width of the list. A tap opens the record (or toggles it while selecting),
 * a long press starts selection, and the row's own ⋯ actions sit on the right. Children that handle their own
 * taps (links, buttons, the actions menu) keep them.
 */
function DataTableMobileRowCard<TData>({
  row,
  mobileConfig,
  renderMobileRow,
  grouped = false,
  onRowClick,
  selecting,
  onStartSelecting,
}: {
  row: Row<TData>;
  mobileConfig?: DataTableMobileConfig;
  renderMobileRow?: (row: Row<TData>) => React.ReactNode;
  /** A row inside a grouped list: no card of its own (the list draws the surface and the hairlines). */
  grouped?: boolean;
  onRowClick?: (row: TData) => void;
  selecting: boolean;
  onStartSelecting: () => void;
}) {
  const labels = useDataTableMobileLabels();
  const cells = row.getVisibleCells();
  const cellById = (id: string) => cells.find((cell) => cell.column.id === id);
  const canSelect = Boolean(cellById("select")) && row.getCanSelect();
  const actionsCell = cellById("actions");
  const selected = row.getIsSelected();

  const { handlers, consumeClick } = useLongPress(
    canSelect && !selecting
      ? () => {
          row.toggleSelected(true);
          onStartSelecting();
        }
      : undefined,
  );

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // A sheet opened from this row (its ⋯ actions) renders in a portal, and React bubbles the portal's events
    // through the row: a tap on the sheet's backdrop must not open the row behind it.
    if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target)) return;
    if (consumeClick()) return;
    if (isInteractiveDescendant(event.target, event.currentTarget)) return;
    if (selecting) {
      if (canSelect) row.toggleSelected();
      return;
    }
    onRowClick?.(row.original);
  };

  let content: React.ReactNode;
  if (renderMobileRow) {
    content = renderMobileRow(row);
  } else {
    const dataCells = cells.filter((cell) => cell.column.id !== "select" && cell.column.id !== "actions");
    const primaryCell = mobileConfig?.primaryField ? cellById(mobileConfig.primaryField) : dataCells[0];
    const secondaryCells = (
      mobileConfig ? (mobileConfig.secondaryFields ?? []).map((id) => cellById(id)) : dataCells.slice(1, 3)
    ).filter((cell): cell is NonNullable<typeof cell> => Boolean(cell));
    content = (
      <div className='min-w-0 space-y-0.5'>
        {primaryCell ? (
          <div className='text-foreground truncate text-[15px] font-bold'>
            {flexRender(primaryCell.column.columnDef.cell, primaryCell.getContext())}
          </div>
        ) : null}
        {secondaryCells.length > 0 ? (
          <div className='text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs'>
            {secondaryCells.map((cell, index) => (
              <React.Fragment key={cell.id}>
                {index > 0 ? <span aria-hidden>·</span> : null}
                <span className='min-w-0 truncate'>{flexRender(cell.column.columnDef.cell, cell.getContext())}</span>
              </React.Fragment>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-state={selected ? "selected" : undefined}
      title={canSelect && !selecting ? labels.longPressHint : undefined}
      onClick={handleClick}
      {...handlers}
      className={cn(
        // The mobile list card: white on the page canvas, no border, rounded, compact (the approved list design).
        "bg-card relative flex items-center gap-3 py-3 pr-2 pl-3.5 transition-colors select-none",
        !grouped && "rounded-[1.125rem]",
        // Inset: an outer ring is clipped by the list's overflow-hidden frame at the card edges.
        "data-[state=selected]:bg-primary/5 data-[state=selected]:ring-1 data-[state=selected]:ring-inset data-[state=selected]:ring-primary/40",
        (onRowClick || selecting) && "cursor-pointer active:bg-muted",
      )}
    >
      {selecting && canSelect ? (
        <span
          aria-hidden
          className={cn(
            "flex size-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors",
            selected ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40",
          )}
        >
          {selected ? <CheckIcon className='size-3.5' strokeWidth={3} /> : null}
        </span>
      ) : null}
      <div className='min-w-0 flex-1'>{content}</div>
      {!selecting && actionsCell ? (
        <div className='shrink-0'>{flexRender(actionsCell.column.columnDef.cell, actionsCell.getContext())}</div>
      ) : !selecting && onRowClick ? (
        <ChevronRightIcon aria-hidden className='text-muted-foreground/60 mr-1.5 size-4 shrink-0' />
      ) : null}
      {canSelect ? (
        <input
          type='checkbox'
          className='sr-only'
          checked={selected}
          onChange={() => {
            if (!selecting) onStartSelecting();
            row.toggleSelected();
          }}
          aria-label={labels.select}
        />
      ) : null}
    </div>
  );
}

/** Whether a tap landed on something that handles itself (link, button, input, menu) inside the row card. */
function isInteractiveDescendant(target: EventTarget, row: Element): boolean {
  if (!(target instanceof Element)) return false;
  const interactive = target.closest("a, button, input, select, textarea, label, [role='menuitem'], [role='button']");
  return Boolean(interactive && interactive !== row && row.contains(interactive));
}

function getColumnDefId<TData>(columnDef: ColumnDef<TData>): string | undefined {
  if (typeof columnDef.id === "string" && columnDef.id.length > 0) {
    return columnDef.id;
  }

  if ("accessorKey" in columnDef && typeof columnDef.accessorKey === "string") {
    return columnDef.accessorKey;
  }

  return undefined;
}

function renderHeaderContent<TData>(header: Header<TData, unknown>) {
  if (header.isPlaceholder) return null;
  const content = flexRender(header.column.columnDef.header, header.getContext());
  if (content === null || content === undefined || content === "") return content;
  if (React.isValidElement(content) && content.type === DataTableColumnHeader) return content;
  if (/^(select|selection|expand|expander)$/i.test(header.column.id)) return content;
  // A column may declare `meta.align` to force header alignment (so header + cells run the same direction),
  // overriding the numeric-id heuristic.
  const metaAlign = (header.column.columnDef.meta as { align?: "left" | "right" } | undefined)?.align;
  return <DataTableAutomaticColumnHeader columnId={header.column.id} label={content} align={metaAlign} />;
}

/**
 * Cell content for the desktop body, grouping-aware:
 * - grouped cell → expand/collapse toggle + group value + child count
 * - aggregated cell → the column's `aggregatedCell` (falls back to `cell`) — the rolled-up subtotal
 * - placeholder cell → blank (the value already shows on the group header)
 * - otherwise → the normal cell renderer
 * For non-grouped tables every cell falls straight through to the normal renderer.
 */
function renderDataCellContent<TData>(cell: Cell<TData, unknown>) {
  // The grouping hierarchy lives in the synthetic group column (see AutoGroupCell); the source grouped columns are
  // hidden. Here we only need: aggregated → the rolled-up value, placeholder → blank, otherwise the normal cell.
  if (cell.getIsAggregated()) {
    return flexRender(cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell, cell.getContext());
  }

  if (cell.getIsPlaceholder()) {
    return null;
  }

  return flexRender(cell.column.columnDef.cell, cell.getContext());
}

/**
 * The single indented hierarchy column rendered while grouping is active (AG-Grid single-column layout):
 * - group rows → expand/collapse chevron + the grouped value (rendered via the source column's `cell`, so dates,
 *   enums and currency stay formatted) + a leaf count,
 * - leaf rows → the {@link leafColumnId} value indented one level deeper (when configured),
 * - otherwise → blank.
 */
function AutoGroupCell<TData>({ row, leafColumnId }: { row: Row<TData>; leafColumnId?: string }) {
  const indentRem = row.depth * DATA_TABLE_GROUP_INDENT_REM;

  if (row.getIsGrouped()) {
    const groupingCell = row.getAllCells().find((cell) => cell.column.id === row.groupingColumnId);
    return (
      <button
        type='button'
        onClick={row.getToggleExpandedHandler()}
        className='flex w-full min-w-0 items-center gap-1.5 text-left'
        style={{ paddingLeft: `${indentRem}rem` }}
      >
        <ChevronRightIcon
          className={cn(
            "text-muted-foreground size-4 shrink-0 transition-transform",
            row.getIsExpanded() && "rotate-90",
          )}
        />
        <span className='min-w-0 truncate font-medium'>
          {groupingCell
            ? flexRender(groupingCell.column.columnDef.cell, groupingCell.getContext())
            : String(row.groupingValue ?? "")}
        </span>
        <span className='text-muted-foreground shrink-0 text-xs font-normal tabular-nums'>
          {row.getLeafRows().length}
        </span>
      </button>
    );
  }

  if (leafColumnId) {
    const leafCell = row.getAllCells().find((cell) => cell.column.id === leafColumnId);
    return (
      <div className='min-w-0 truncate' style={{ paddingLeft: `${indentRem + DATA_TABLE_GROUP_INDENT_REM}rem` }}>
        {leafCell ? flexRender(leafCell.column.columnDef.cell, leafCell.getContext()) : null}
      </div>
    );
  }

  return null;
}

/**
 * The row-group panel shown above a grouping-enabled table: a chip per grouped column (removable + drag-to-reorder),
 * an "Add group" menu of the remaining groupable columns, and expand/collapse-all. Empty grouping renders a hint.
 */
function DataTableGroupPanel({
  grouping,
  onChange,
  options,
  resolveLabel,
  labels,
  allExpanded,
  onExpandAll,
  onCollapseAll,
}: {
  grouping: string[];
  onChange: (next: string[]) => void;
  options: string[];
  resolveLabel: (columnId: string) => string;
  labels: DataTableGroupPanelLabels;
  allExpanded: boolean;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}) {
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);

  const move = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    const next = [...grouping];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return;
    next.splice(to, 0, moved);
    onChange(next);
  };

  return (
    <div className='flex min-w-0 flex-wrap items-center gap-2 rounded-xl border border-border/60 bg-muted/30 px-3 py-2'>
      <Rows3Icon className='text-muted-foreground size-4 shrink-0' />

      {grouping.length === 0 ? (
        <span className='text-muted-foreground min-w-0 flex-1 truncate text-sm'>{labels.empty}</span>
      ) : (
        <div className='flex min-w-0 flex-wrap items-center gap-1.5'>
          {grouping.map((columnId, index) => (
            <span
              key={columnId}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => {
                if (dragIndex !== null) move(dragIndex, index);
                setDragIndex(null);
              }}
              onDragEnd={() => setDragIndex(null)}
              className={cn(
                "bg-background inline-flex cursor-grab items-center gap-1 rounded-md border border-border/70 py-1 pr-1 pl-1.5 text-sm shadow-sm active:cursor-grabbing",
                dragIndex === index && "opacity-50",
              )}
            >
              <GripVerticalIcon className='text-muted-foreground size-3.5 shrink-0' />
              <span className='max-w-[10rem] truncate'>{resolveLabel(columnId)}</span>
              <button
                type='button'
                aria-label={labels.removeGroup}
                onClick={() => onChange(grouping.filter((id) => id !== columnId))}
                className='text-muted-foreground hover:text-foreground hover:bg-muted/60 ml-0.5 inline-flex size-4 items-center justify-center rounded'
              >
                <XIcon className='size-3' />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className='ml-auto flex shrink-0 items-center gap-1'>
        {options.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type='button' variant='ghost' size='sm' className='text-muted-foreground gap-1.5'>
                <PlusIcon className='size-4' />
                {labels.addGroup}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='max-h-72 w-52 overflow-auto'>
              {options.map((columnId) => (
                <DropdownMenuItem key={columnId} onSelect={() => onChange([...grouping, columnId])}>
                  {resolveLabel(columnId)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {grouping.length > 0 ? (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='text-muted-foreground gap-1.5'
            onClick={allExpanded ? onCollapseAll : onExpandAll}
          >
            {allExpanded ? <ChevronsDownUpIcon className='size-4' /> : <ChevronsUpDownIcon className='size-4' />}
            {allExpanded ? labels.collapseAll : labels.expandAll}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function renderLoadingTableRows<TData>(columns: Column<TData, unknown>[], rowCount: number, cellClassName?: string) {
  return Array.from({ length: rowCount }).map((_, rowIndex) => (
    <TableRow key={`loading-row-${rowIndex}`} className='group bg-card'>
      {columns.map((column) => {
        const pinningStyle = getPinnedColumnStyle(column, "cell");
        return (
          <TableCell
            key={`loading-cell-${rowIndex}-${column.id}`}
            className={cn(
              pinningStyle.className,
              "!overflow-visible !whitespace-normal",
              cellClassName,
            )}
            style={resolveColumnStyle(column, pinningStyle.style)}
          >
            <Skeleton aria-hidden='true' className='block h-4 min-h-4 min-w-[4rem] w-full max-w-full rounded-md' />
          </TableCell>
        );
      })}
    </TableRow>
  ));
}
